import React, { useState, useRef, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { Form, Formik, FormikHelpers } from 'formik';
import { useNavigate } from 'react-router-dom';
import {
  useTranslator,
  FormikInput as Input,
  useToast,
  useAppContext,
  withMobileAppMode,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Alert } from 'reactstrap';
import validationSchema from './validationSchema';
import { normalizeDescribeMeal } from '../helpers/normalizers';

interface DescribeMealContainerProps {
  extractIngredients: (input: any) => Promise<any>;
  cancelExtractIngredients: () => void;
  mobileAppMode: boolean;
  fromUserMeals?: boolean;
}

const DescribeMealVoiceContainer: React.FC<DescribeMealContainerProps> = ({
  extractIngredients,
  cancelExtractIngredients,
  mobileAppMode,
  fromUserMeals,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const navigate = useNavigate();
  const isCanceledRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    images: {
      aiStarLightIcon,
      microphoneOnIcon,
      microphoneOffIcon,
      resetPrimaryIcon,
      editPrimaryIcon,
    },
  } = useAppContext();

  const [noFoodError, setNoFoodError] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [description, setDescription] = useState('');
  const recognitionRef = useRef<any>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const alertRef2 = useRef<HTMLDivElement>(null);

  const formikValues = {
    description: '',
  };

  // Auto-scroll textarea to bottom when description changes
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.scrollTop = inputRef.current.scrollHeight;
    }
  }, [description]);

  // Scroll to alert when noFoodError appears
  useEffect(() => {
    if (noFoodError && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [noFoodError]);

  // Scroll to alert when rateLimitExceeded appears
  useEffect(() => {
    if (rateLimitExceeded && alertRef2.current) {
      alertRef2.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [rateLimitExceeded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

      setTimeout(() => {
        if (buttonRef.current) {
          buttonRef.current.click();
        }
      }, 0);
    };
  }, []);

  const handleRestart = (
    setFieldValue: (field: string, value: string, shouldValidate?: boolean) => void,
    setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void
  ) => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setShowInput(false);
    setNoFoodError(false);
    setRateLimitExceeded(false);
    setIsRecording(false);
    setFieldValue('description', '', false);
    setFieldTouched('description', false, false);
    setDescription('');

    setTimeout(() => {
      if (buttonRef.current) {
        buttonRef.current.click();
      }
    }, 100);
  };

  const isAndroidChrome = (userAgent: string) => {
    return /Android/i.test(userAgent);
  };

  const handleEdit = () => {
    // If already recording, stop it
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }

    inputRef.current?.focus();
  };

  const handleVoiceRecognition = (
    setFieldValue: (field: string, value: string) => void,
    currentValue: string
  ) => {
    // Check if browser supports Web Speech API
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      toastError(t('tracker.describeFood.byVoice.notSupported'));
      return;
    }

    // If already recording, stop it
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      return;
    }

    setShowInput(true);
    setNoFoodError(false);
    setRateLimitExceeded(false);

    // Create new recognition instance
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    // Configure recognition
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = localStorage.getItem('i18nextLng') || 'en-US';

    // Start with the current value instead of empty string
    let finalTranscript = '';

    recognition.onresult = (event: any) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          // check if is android chrome
          if (isAndroidChrome(navigator.userAgent)) {
            finalTranscript = transcript;
          } else {
            finalTranscript += (finalTranscript ? ' ' : '') + transcript;
          }
        } else {
          interimTranscript += transcript;
        }
      }

      // Update field value with combined transcript
      const combinedValue =
        currentValue +
        (finalTranscript ? ' ' + finalTranscript : '') +
        (interimTranscript ? ' ' + interimTranscript : '');
      // if android
      // const combinedValue = currentValue + ' ' + finalTranscript + (interimTranscript ? ' ' + interimTranscript : '');
      const trimmedValue = combinedValue.trim();
      setFieldValue('description', trimmedValue);
      setDescription(trimmedValue);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);

      // no access
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toastError(t('tracker.describeFood.voice.noAccessMicrophone.notification'));
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    // Start recognition
    try {
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recognition:', error);
      toastError();
    }
  };

  const handleSubmit = async (
    values: typeof formikValues,
    { setSubmitting }: FormikHelpers<typeof formikValues>
  ) => {
    setNoFoodError(false);
    setRateLimitExceeded(false);

    // If already recording, stop it
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }

    // Prevent retry if user canceled the previous request
    if (isCanceledRef.current) {
      isCanceledRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const response = await extractIngredients({
        mealDescription: values.description,
      });

      if (response.__typename === 'ExtractedIngredients') {
        sessionStorage.setItem('tracker_depth', '2');

        // calculate random id
        const dietMealId = Math.floor(Math.random() * 1000000000);

        const normalizedProps = normalizeDescribeMeal({
          ...response,
          dietMealId,
        });

        setShowInput(false);

        if (fromUserMeals) {
          navigate(`/meals/user-meals/describe-meal/meal-details/${dietMealId}`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        } else {
          navigate(`/tracker/describe-meal/meal-details/${dietMealId}`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        }
      } else if (response.__typename === 'NotFoodError') {
        setNoFoodError(true);
      } else if (response.__typename === 'RateLimitExceeded') {
        setRateLimitExceeded(true);
      } else {
        toastError();
      }
    } catch (error) {
      // Don't show error if operation was canceled
      // Note: setSubmitting(false) is already called in handleCancel
      if (isCanceledRef.current || (error as Error)?.name === 'AbortError') {
        return;
      }
      // For other errors, reset submitting state and show error
      setSubmitting(false);
      toastError();
    }
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.describeFood.byVoice.navbarTitle')}</TitleBar>

      <Formik
        onSubmit={handleSubmit}
        initialValues={formikValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
      >
        {({ isValid, values, isSubmitting, setSubmitting, setFieldValue, setFieldTouched }) => {
          let buttonClasses = 'voice-button';
          if (isRecording) {
            buttonClasses += ' is-recording';
          }
          if (isSubmitting) {
            buttonClasses += ' is-submitting';
          }

          return (
            <Form>
              <div className="page-template-tracker edit-meal-page">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col md={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <Button
                            className={buttonClasses}
                            onClick={() =>
                              handleVoiceRecognition(setFieldValue, values.description)
                            }
                            type="button"
                            innerRef={buttonRef}
                          >
                            {isSubmitting ? (
                              <div className="loader-container">
                                <p className="mb-0 medium">
                                  {t('tracker.describeFood.loader.analyzing')}
                                </p>
                              </div>
                            ) : (
                              <>
                                <img
                                  src={isRecording ? microphoneOnIcon : microphoneOffIcon}
                                  alt=""
                                />
                                <p className="run-microphone">
                                  {t('tracker.describeFood.voice.runMicrophone')}
                                </p>
                                <span style={{ '--i': 0 } as React.CSSProperties}></span>
                                <span style={{ '--i': 1 } as React.CSSProperties}></span>
                                <span style={{ '--i': 2 } as React.CSSProperties}></span>
                                <span style={{ '--i': 3 } as React.CSSProperties}></span>
                              </>
                            )}
                          </Button>

                          {showInput && (
                            <Input
                              name="description"
                              type="textarea"
                              placeholder={t('tracker.describeFood.voice.inputPlaceholder')}
                              required
                              translateErrMsg
                              className="voice-input"
                              innerRef={inputRef}
                            />
                          )}

                          {noFoodError && (
                            <div ref={alertRef}>
                              <Alert color="warning" className="mb-0" fade={false}>
                                <p className="mb-0">
                                  {t('tracker.describeFood.analyze.noFood.notification')}
                                </p>
                              </Alert>
                            </div>
                          )}

                          {rateLimitExceeded && (
                            <div ref={alertRef2}>
                              <Alert color="danger" className="mb-0" fade={false}>
                                <p className="mb-0">{t('tracker.limitUsageError.notification')}</p>
                              </Alert>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                {values.description && !isSubmitting && (
                  <div className="sticky-footer">
                    <Container className="px-3">
                      <Row>
                        <Col xs="12" md={{ size: 4, offset: 4 }}>
                          <Row className="mb-s">
                            <Col xs="6" className="text-center">
                              <Button
                                type="button"
                                size="sm"
                                color="link"
                                onClick={() => handleRestart(setFieldValue, setFieldTouched)}
                              >
                                <img src={resetPrimaryIcon} className="me-2" alt="" />
                                {t('tracker.describeFood.voice.restartButton')}
                              </Button>
                            </Col>
                            <Col xs="6" className="text-center">
                              <Button type="button" size="sm" color="link" onClick={handleEdit}>
                                <img src={editPrimaryIcon} className="me-2" alt="" />
                                {t('tracker.describeFood.voice.editButton')}
                              </Button>
                            </Col>
                          </Row>
                          <Button
                            type="submit"
                            color="primary"
                            disabled={!isValid}
                            className="w-100"
                          >
                            <img
                              src={aiStarLightIcon}
                              alt=""
                              style={{ marginTop: '-3px', marginBottom: '-3px' }}
                              className="me-2"
                            />
                            {t('tracker.describeFood.button.analyze')}
                          </Button>
                        </Col>
                      </Row>
                    </Container>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default withMobileAppMode(DescribeMealVoiceContainer);
